import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ConditionNode from "./Nodes/ConditionNode";
import ActionNode from "./Nodes/ActionNode";
import StartNode from "./Nodes/StartNode";
import EndNode from "./Nodes/EndNode";
import "./FlowEditor.css";

import {
  createWorkflowApi,
  getWorkflowByIdApi,
  updateWorkflowApi,
} from "../../../apiservice/ats";
import { fetchRoles, fetchUsers } from "./workflowThunks";

const nodeTypes = {
  condition: ConditionNode,
  action: ActionNode,
  start: StartNode,
  end: EndNode,
};

export default function FlowEditor({ onSaved }) {
  const navigate = useNavigate();
  const { id: workflowIdParam } = useParams();
  const workflowId = workflowIdParam || "New";
  const dispatch = useDispatch();

  const [workflowName, setWorkflowName] = useState("");
  const [recordType, setRecordType] = useState("Job");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  /* ------------------- Constants ------------------- */

  const fieldsByRecordType = useMemo(
    () => ({
      Job: ["status", "priority", "department", "location"],
      Candidate: ["name", "experience", "status", "source"],
      Application: ["app_status", "submitted_date", "job_id"],
      Sales: ["amount", "stage", "assigned_to", "region"],
    }),
    []
  );

  /* ------------------- Redux Data ------------------- */

  const roleDataList = useSelector((s) => s.workflow.roles || []);
  const userDataList = useSelector((s) => s.workflow.users || []);

  const roles = useMemo(
    () => roleDataList.map((r) => r.role_name),
    [roleDataList]
  );

  const users = useMemo(
    () => userDataList.map((u) => u.email),
    [userDataList]
  );

  /* ------------------- Stable Remove Node ------------------- */

  const removeNode = useCallback(
    (id) => {
      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((eds) =>
        eds.filter((e) => e.source !== id && e.target !== id)
      );
    },
    [setNodes, setEdges]
  );

  /* ------------------- Load Roles & Users ------------------- */

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchUsers());
  }, [dispatch]);

  /* ------------------- Load Workflow (Edit Mode) ------------------- */

  useEffect(() => {
    if (workflowId === "New") {
      setNodes([
        {
          id: "start-1",
          type: "start",
          position: { x: 100, y: 100 },
          data: { label: "Start" },
        },
      ]);
      setEdges([]);
      return;
    }

    const loadWorkflow = async () => {
      try {
        const res = await getWorkflowByIdApi(workflowId);
        const data = res.data;

        setWorkflowName(data.name || "");
        setRecordType(data.record_type || "Job");

        const nodesWithFix = (data.graph?.nodes || []).map((node) => {
          if (node.type === "condition" || node.type === "action") {
            return {
              ...node,
              data: {
                ...node.data,
                recordType: data.record_type,
                fields: fieldsByRecordType[data.record_type],
                roleDataList,
                userDataList,
                onRemove: () => removeNode(node.id),
              },
            };
          }

          return {
            ...node,
            data: {
              ...node.data,
              onRemove: () => removeNode(node.id),
            },
          };
        });

        setNodes(nodesWithFix);
        setEdges(data.graph?.edges || []);
      } catch (err) {
        console.error(err);
        message.error("Failed to load workflow");
      }
    };

    loadWorkflow();
  }, [workflowId]);

  /* ------------------- Sync Nodes on Record Type Change ------------------- */

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === "condition" || node.type === "action") {
          return {
            ...node,
            data: {
              ...node.data,
              recordType,
              fields: fieldsByRecordType[recordType],
              roleDataList,
              userDataList,
            },
          };
        }
        return node;
      })
    );
  }, [recordType, roleDataList, userDataList]);

  /* ------------------- Add Nodes ------------------- */

  const addConditionNode = () => {
    const id = `cond-${Date.now()}`;
    setNodes((nds) =>
      nds.concat({
        id,
        type: "condition",
        position: { x: 300, y: 200 },
        data: {
          label: "Condition",
          recordType,
          field: "status",
          operator: "equals",
          value: "",
          fields: fieldsByRecordType[recordType],
          onRemove: () => removeNode(id),
        },
      })
    );
  };

  const addActionNode = () => {
    const id = `action-${Date.now()}`;
    setNodes((nds) =>
      nds.concat({
        id,
        type: "action",
        position: { x: 500, y: 200 },
        data: {
          label: "Action",
          actionType: "Send Email",
          recordType,
          fields: fieldsByRecordType[recordType],
          roleDataList,
          userDataList,
          onRemove: () => removeNode(id),
        },
      })
    );
  };

  /* ------------------- Connections ------------------- */

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    []
  );

  /* ------------------- Save Workflow ------------------- */

  const saveWorkflow = async () => {
    if (!workflowName) {
      message.warning("Please enter workflow name");
      return;
    }

    try {
      const graph = { nodes, edges };
      const payload = { name: workflowName, record_type: recordType, nodes, edges, graph };

      if (workflowId === "New") {
        await createWorkflowApi(payload);
      } else {
        await updateWorkflowApi(workflowId, payload);
      }

      message.success("Workflow saved successfully");
      navigate("/ats/workflows");
      onSaved?.();
    } catch (err) {
      console.error(err);
      message.error("Failed to save workflow");
    }
  };

  /* ------------------- UI ------------------- */

  return (
    <div style={{ height: "calc(100vh - 160px)", minHeight: 500 }}>
      <div className="toolbar">
        <input
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          placeholder="Workflow Name"
          className="workflow-name-input"
        />

        <select
          value={recordType}
          onChange={(e) => setRecordType(e.target.value)}
          className="record-type-select"
        >
          <option value="Job">Job</option>
          <option value="Candidate">Candidate</option>
          <option value="Application">Application</option>
          <option value="Sales">Sales</option>
        </select>

        <button className="toolbar-btn" onClick={addConditionNode}>Add Condition</button>
        <button className="toolbar-btn" onClick={addActionNode}>Add Action</button>
        <button className="save-btn" onClick={saveWorkflow}>
          Save
        </button>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}
